/**
 * Telefon-Vorwahlen für den PhoneInput. Europa komplett plus die wichtigsten
 * Außer-EU-Ziele; die Kernmärkte stehen oben (`pinned`), der Rest folgt
 * alphabetisch. `name` ist das deutsche Label, `alt` das Endonym / die
 * englische Form — beides wird durchsucht, damit "Griechenland", "Greece"
 * und "Ελλάδα" alle treffen.
 */
export type DialCode = {
  code: string; // ISO-2 (uppercase)
  iso: string; // flag-icons class suffix (lowercase)
  dial: string; // "+49"
  name: string; // deutsches Label
  alt?: string; // Endonym / englisch (nur Suche)
  pinned?: boolean;
};

const PINNED: DialCode[] = [
  { code: "DE", iso: "de", dial: "+49", name: "Deutschland", alt: "Germany", pinned: true },
  { code: "AT", iso: "at", dial: "+43", name: "Österreich", alt: "Austria", pinned: true },
  { code: "CH", iso: "ch", dial: "+41", name: "Schweiz", alt: "Switzerland Suisse Svizzera", pinned: true },
  { code: "NL", iso: "nl", dial: "+31", name: "Niederlande", alt: "Nederland Netherlands", pinned: true },
  { code: "BE", iso: "be", dial: "+32", name: "Belgien", alt: "België Belgique Belgium", pinned: true },
  { code: "FR", iso: "fr", dial: "+33", name: "Frankreich", alt: "France", pinned: true },
  { code: "IT", iso: "it", dial: "+39", name: "Italien", alt: "Italia Italy", pinned: true },
  { code: "PL", iso: "pl", dial: "+48", name: "Polen", alt: "Polska Poland", pinned: true },
  { code: "CZ", iso: "cz", dial: "+420", name: "Tschechien", alt: "Česko Czechia", pinned: true },
  { code: "DK", iso: "dk", dial: "+45", name: "Dänemark", alt: "Danmark Denmark", pinned: true },
  { code: "LU", iso: "lu", dial: "+352", name: "Luxemburg", alt: "Luxembourg", pinned: true },
  { code: "ES", iso: "es", dial: "+34", name: "Spanien", alt: "España Spain", pinned: true },
  { code: "GB", iso: "gb", dial: "+44", name: "Vereinigtes Königreich", alt: "United Kingdom UK England", pinned: true },
];

const REST: DialCode[] = [
  { code: "AL", iso: "al", dial: "+355", name: "Albanien", alt: "Shqipëria Albania" },
  { code: "AD", iso: "ad", dial: "+376", name: "Andorra" },
  { code: "BY", iso: "by", dial: "+375", name: "Belarus", alt: "Беларусь" },
  { code: "BA", iso: "ba", dial: "+387", name: "Bosnien und Herzegowina", alt: "Bosna i Hercegovina Bosnia" },
  { code: "BG", iso: "bg", dial: "+359", name: "Bulgarien", alt: "България Bulgaria" },
  { code: "CN", iso: "cn", dial: "+86", name: "China", alt: "中国" },
  { code: "EE", iso: "ee", dial: "+372", name: "Estland", alt: "Eesti Estonia" },
  { code: "FI", iso: "fi", dial: "+358", name: "Finnland", alt: "Suomi Finland" },
  { code: "GR", iso: "gr", dial: "+30", name: "Griechenland", alt: "Ελλάδα Greece" },
  { code: "IE", iso: "ie", dial: "+353", name: "Irland", alt: "Ireland Éire" },
  { code: "IS", iso: "is", dial: "+354", name: "Island", alt: "Ísland Iceland" },
  { code: "XK", iso: "xk", dial: "+383", name: "Kosovo", alt: "Kosova" },
  { code: "HR", iso: "hr", dial: "+385", name: "Kroatien", alt: "Hrvatska Croatia" },
  { code: "LV", iso: "lv", dial: "+371", name: "Lettland", alt: "Latvija Latvia" },
  { code: "LI", iso: "li", dial: "+423", name: "Liechtenstein" },
  { code: "LT", iso: "lt", dial: "+370", name: "Litauen", alt: "Lietuva Lithuania" },
  { code: "MT", iso: "mt", dial: "+356", name: "Malta" },
  { code: "MD", iso: "md", dial: "+373", name: "Moldau", alt: "Moldova" },
  { code: "MC", iso: "mc", dial: "+377", name: "Monaco" },
  { code: "ME", iso: "me", dial: "+382", name: "Montenegro", alt: "Crna Gora" },
  { code: "MK", iso: "mk", dial: "+389", name: "Nordmazedonien", alt: "Северна Македонија North Macedonia" },
  { code: "NO", iso: "no", dial: "+47", name: "Norwegen", alt: "Norge Norway" },
  { code: "PT", iso: "pt", dial: "+351", name: "Portugal" },
  { code: "RO", iso: "ro", dial: "+40", name: "Rumänien", alt: "România Romania" },
  { code: "SM", iso: "sm", dial: "+378", name: "San Marino" },
  { code: "SE", iso: "se", dial: "+46", name: "Schweden", alt: "Sverige Sweden" },
  { code: "RS", iso: "rs", dial: "+381", name: "Serbien", alt: "Srbija Србија Serbia" },
  { code: "SK", iso: "sk", dial: "+421", name: "Slowakei", alt: "Slovensko Slovakia" },
  { code: "SI", iso: "si", dial: "+386", name: "Slowenien", alt: "Slovenija Slovenia" },
  { code: "TR", iso: "tr", dial: "+90", name: "Türkei", alt: "Türkiye Turkey" },
  { code: "UA", iso: "ua", dial: "+380", name: "Ukraine", alt: "Україна" },
  { code: "HU", iso: "hu", dial: "+36", name: "Ungarn", alt: "Magyarország Hungary" },
  { code: "US", iso: "us", dial: "+1", name: "USA", alt: "United States Vereinigte Staaten" },
  { code: "CY", iso: "cy", dial: "+357", name: "Zypern", alt: "Κύπρος Cyprus" },
];

/** Kernmärkte oben, Rest alphabetisch nach deutschem Namen. */
export const DIAL_CODES: DialCode[] = [
  ...PINNED,
  ...[...REST].sort((a, b) => a.name.localeCompare(b.name, "de")),
];

export function dialCodeFor(code: string | undefined | null): DialCode | undefined {
  if (!code) return undefined;
  const cc = code.toUpperCase();
  return DIAL_CODES.find((d) => d.code === cc);
}

/** Längster passender Vorwahl-Prefix (+49 vor +4…), sonst undefined. */
export function dialCodeForNumber(value: string): DialCode | undefined {
  const v = value.trim();
  if (!v.startsWith("+")) return undefined;
  let best: DialCode | undefined;
  for (const d of DIAL_CODES) {
    if (v.startsWith(d.dial) && (!best || d.dial.length > best.dial.length)) best = d;
  }
  return best;
}

export function matchesDialQuery(d: DialCode, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const digits = q.replace(/^\+/, "");
  return (
    d.name.toLowerCase().includes(q) ||
    (d.alt?.toLowerCase().includes(q) ?? false) ||
    d.code.toLowerCase().includes(q) ||
    (digits.length > 0 && /^\d+$/.test(digits) && d.dial.slice(1).startsWith(digits))
  );
}
